import type { DuplicateContractMutationOptions } from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { UUID } from '~/types/common';

import { queryKeysContract } from '../queryKeys';

import { duplicateContract } from './duplicateContract';

/**
 * Custom hook for duplicating an existing contract.
 * Creates a new contract with the same data as the original.
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/duplicate_contract_contracts__contract_id__duplicate_post
 */
export const useMutationDuplicateContract = (
  options?: DuplicateContractMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contractId: UUID) => duplicateContract(contractId),
    ...options,
    async onSuccess(data, variables, onMutateResult, context) {
      await options?.onSuccess?.(data, variables, onMutateResult, context);

      return queryClient.invalidateQueries({
        queryKey: [queryKeysContract.base()],
      });
    },
  });
};
